import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Download,
  Play,
  Clock,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { formatCalendarDate, formatInstantDay } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";

/* ────────────── types ────────────── */

interface CourseData {
  id: string;
  title: string;
  description: string;
  video_url: string;
  passing_score: number;
  validity_period_months: number;
  questions_per_quiz: number;
  max_attempts_per_day: number;
  questions: QuizQuestion[];
  attempts_today: number;
}

interface QuizQuestion {
  id: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  sort_order: number;
}

interface AttemptResult {
  passed: boolean;
  score: number;
  passing_score: number;
  certification?: {
    id: string;
    certificate_number: string;
    issued_at: string;
    expires_at: string;
  };
  question_results?: {
    question_text: string;
    selected_answer: string;
    correct_answer: string;
    is_correct: boolean;
  }[];
  attempts_remaining_today?: number;
}

type Step = 1 | 2 | 3;

/* ────────────── helpers ────────────── */

const LABELS = ["A", "B", "C", "D"] as const;

/**
 * SPLIT, not a swap. This helper was fed BOTH `certification.issued_at` and
 * `certification.expires_at` on adjacent lines, so pointing it at the
 * calendar-day path would have converted a genuine instant — the one thing
 * this rule must never induce.
 *
 * `issued_at` is an INSTANT (the moment the quiz was passed), so `new Date()`
 * stays here and is correct.
 *
 * This is the same pair, on the same `Certification` payload from the same
 * /safety/training/certifications endpoint, that src/pages/safety/MyTrainingScreen.tsx
 * already ships — fmtDate/fmtExpiry there, named the same way on purpose so the
 * two screens cannot drift about one certificate.
 */
/** `issued_at` is an INSTANT (timestamptz): shown as the TENANT's day. */
const fmtDate = (d: string, timeZone: string | null) =>
  formatInstantDay(d, timeZone, { month: "short", day: "numeric", year: "numeric" }) ?? "—";

/** `expires_at` is day-precision, not an instant — see eslint.legacy-patterns.js. */
const fmtExpiry = (d: string) =>
  formatCalendarDate(d, { month: "short", day: "numeric", year: "numeric" }) ?? "—";

const isYouTubeUrl = (url: string) => {
  return /youtube\.com|youtu\.be/i.test(url);
};

const getYouTubeEmbedUrl = (url: string) => {
  // Handle various YouTube URL formats
  let videoId = "";
  const patterns = [
    /(?:youtube\.com\/embed\/)([^?&]+)/,
    /(?:youtube\.com\/watch\?v=)([^&]+)/,
    /(?:youtu\.be\/)([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) {
      videoId = m[1];
      break;
    }
  }
  if (!videoId) return url; // fallback: use as-is
  return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}`;
};

/* ────────────── YouTube Player Hook ────────────── */

function useYouTubeProgress(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  videoUrl: string,
  enabled: boolean
) {
  const [progress, setProgress] = useState(0);
  const playerRef = useRef<any>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !isYouTubeUrl(videoUrl)) return;

    // Load YouTube IFrame API
    if (!(window as any).YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }

    const initPlayer = () => {
      if (!iframeRef.current) return;
      playerRef.current = new (window as any).YT.Player(iframeRef.current, {
        events: {
          onStateChange: () => {
            // Start polling when playing
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = window.setInterval(() => {
              if (playerRef.current?.getCurrentTime && playerRef.current?.getDuration) {
                const current = playerRef.current.getCurrentTime();
                const duration = playerRef.current.getDuration();
                if (duration > 0) {
                  setProgress(Math.round((current / duration) * 100));
                }
              }
            }, 1000);
          },
        },
      });
    };

    if ((window as any).YT?.Player) {
      initPlayer();
    } else {
      (window as any).onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, videoUrl, iframeRef]);

  return progress;
}

/* ────────────── Step 1: Introduction ────────────── */

const IntroStep = ({
  course,
  videoProgress,
  onBeginQuiz,
  iframeRef,
  onVideoProgress,
}: {
  course: CourseData;
  videoProgress: number;
  onBeginQuiz: () => void;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onVideoProgress: (p: number) => void;
}) => {
  const hasVideo = !!course.video_url;
  const canBeginQuiz = !hasVideo || videoProgress >= 80;

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (video.duration > 0) {
      onVideoProgress(Math.round((video.currentTime / video.duration) * 100));
    }
  };

  return (
    <div className="space-y-6">
      {/* Course info */}
      <div>
        <h2 className="text-lg font-bold text-foreground">{course.title}</h2>
        {course.description && (
          <p className="text-sm text-muted-foreground mt-2">
            {course.description}
          </p>
        )}
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-lg font-medium">
          <CheckCircle className="w-3.5 h-3.5" />
          Pass: {course.passing_score}%
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-secondary text-muted-foreground rounded-lg font-medium">
          <Clock className="w-3.5 h-3.5" />
          Valid:{" "}
          {course.validity_period_months === 12
            ? "1 Year"
            : course.validity_period_months === 24
              ? "2 Years"
              : course.validity_period_months === 36
                ? "3 Years"
                : `${course.validity_period_months} months`}
        </span>
      </div>

      {/* Video section */}
      {hasVideo ? (
        <div className="space-y-3">
          <div className="rounded-xl overflow-hidden border border-border bg-black aspect-video">
            {isYouTubeUrl(course.video_url) ? (
              <iframe
                ref={iframeRef}
                src={getYouTubeEmbedUrl(course.video_url)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={course.title}
              />
            ) : (
              <video
                className="w-full h-full"
                controls
                onTimeUpdate={handleTimeUpdate}
                src={course.video_url}
              >
                <track kind="captions" />
              </video>
            )}
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Video className="w-3.5 h-3.5" />
                Video Progress
              </span>
              <span className="text-xs font-medium text-foreground">
                {videoProgress}%
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${videoProgress >= 80 ? "bg-green-500" : "bg-primary"}`}
                style={{ width: `${videoProgress}%` }}
              />
            </div>
            {videoProgress < 80 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Watch at least 80% of the video to unlock the quiz
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-secondary/30 p-8 text-center">
          <Video className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No video for this course
          </p>
        </div>
      )}

      {/* Begin Quiz */}
      <button
        onClick={onBeginQuiz}
        disabled={!canBeginQuiz}
        className="h-10 px-6 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
      >
        <Play className="w-4 h-4" />
        Begin Quiz
      </button>
    </div>
  );
};

/* ────────────── Step 2: Quiz ────────────── */

const QuizStep = ({
  questions,
  onSubmit,
}: {
  questions: QuizQuestion[];
  onSubmit: (answers: Record<string, string>) => void;
}) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Date.now() - startTime), 1000);
    return () => clearInterval(t);
  }, [startTime]);

  const question = questions[currentIdx];
  const totalQuestions = questions.length;
  const isLast = currentIdx === totalQuestions - 1;
  const selected = answers[question.id] ?? null;

  const options = [
    { label: "A", text: question.option_a },
    { label: "B", text: question.option_b },
    { label: "C", text: question.option_c },
    { label: "D", text: question.option_d },
  ].filter((o) => o.text);

  const fmtElapsed = () => {
    const s = Math.floor(elapsed / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleNext = () => {
    if (isLast) {
      onSubmit(answers);
    } else {
      setCurrentIdx((i) => i + 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          Question {currentIdx + 1} of {totalQuestions}
        </span>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {fmtElapsed()}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{
            width: `${((currentIdx + 1) / totalQuestions) * 100}%`,
          }}
        />
      </div>

      {/* Question */}
      <div className="py-2">
        <p className="text-base font-medium text-foreground leading-relaxed">
          {question.question_text}
        </p>
      </div>

      {/* Answer options as large cards */}
      <div className="space-y-3">
        {options.map((opt) => {
          const isSelected = selected === opt.label;
          return (
            <button
              key={opt.label}
              onClick={() =>
                setAnswers((prev) => ({ ...prev, [question.id]: opt.label }))
              }
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-colors ${
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-border bg-fieldiq-card hover:border-primary/30"
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {opt.label}
                </span>
                <span className="text-sm text-foreground">{opt.text}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Next / Submit */}
      <button
        onClick={handleNext}
        disabled={!selected}
        className="h-10 px-6 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
      >
        {isLast ? "Submit Answers" : "Next Question"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ────────────── Step 3: Results ────────────── */

const ResultsStep = ({
  result,
  course,
  onRetry,
  onBack,
}: {
  result: AttemptResult;
  course: CourseData;
  onRetry: () => void;
  onBack: () => void;
}) => {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();

  const handleDownload = async () => {
    if (!result.certification?.id) return;
    try {
      const res = await api.safety.downloadCertPdf(result.certification.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.certification.certificate_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download certificate");
    }
  };

  if (result.passed) {
    return (
      <div className="space-y-6 text-center max-w-lg mx-auto">
        <div className="w-20 h-20 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">
            Congratulations!
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            You passed with {result.score}%
          </p>
        </div>

        {/* Certification card */}
        {result.certification && (
          <div className="bg-fieldiq-card border border-green-500/30 rounded-xl p-5 text-left space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">
                Certificate
              </span>
            </div>
            <h3 className="text-sm font-bold text-foreground">
              {course.title}
            </h3>
            <div className="text-xs text-muted-foreground font-mono">
              {result.certification.certificate_number}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Issued: {fmtDate(result.certification.issued_at, timeZone)}</span>
              <span>Expires: {fmtExpiry(result.certification.expires_at)}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-3">
          {result.certification && (
            <button
              onClick={handleDownload}
              className="h-9 px-4 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-secondary/50 transition-colors inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Certificate
            </button>
          )}
          <button
            onClick={onBack}
            className="h-9 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to My Training
          </button>
        </div>
      </div>
    );
  }

  // Failed
  const canRetry =
    result.attempts_remaining_today == null ||
    result.attempts_remaining_today > 0;

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-foreground mt-4">Not quite</h2>
        <p className="text-sm text-muted-foreground mt-1">
          You scored {result.score}%, need {result.passing_score}% to pass
        </p>
      </div>

      {/* Wrong answers review */}
      {result.question_results && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            Review incorrect answers
          </h3>
          {result.question_results
            .filter((q) => !q.is_correct)
            .map((q, idx) => (
              <div
                key={idx}
                className="bg-fieldiq-card border border-border rounded-lg p-4 space-y-2"
              >
                <p className="text-sm text-foreground">{q.question_text}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                    Your answer: {q.selected_answer}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                    Correct: {q.correct_answer}
                  </span>
                </div>
              </div>
            ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        {canRetry ? (
          <button
            onClick={onRetry}
            className="h-9 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Try Again
          </button>
        ) : (
          <div className="flex items-center gap-2 text-sm text-amber-400">
            <AlertTriangle className="w-4 h-4" />
            Come back tomorrow — daily attempt limit reached
          </div>
        )}
        <button
          onClick={onBack}
          className="h-9 px-4 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-secondary/50 transition-colors"
        >
          Back to My Training
        </button>
      </div>
    </div>
  );
};

/* ────────────── Main Component ────────────── */

const TakeTrainingScreen = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [videoProgress, setVideoProgress] = useState(0);
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const { data: course, isLoading } = useQuery<CourseData>({
    queryKey: ["training-take", courseId],
    queryFn: () =>
      api.safety
        .takeTrainingCourse(courseId!)
        .then((r: any) => {
          const payload = r.data?.data ?? r.data;
          return {
            ...payload.course,
            questions: payload.questions,
            attempts_today: payload.attempts_today,
            max_attempts_per_day: payload.max_attempts_per_day,
          };
        }),
    enabled: !!courseId,
    staleTime: 60_000,
  });

  // YouTube progress tracking
  const ytProgress = useYouTubeProgress(
    iframeRef,
    course?.video_url ?? "",
    step === 1 && !!course?.video_url && isYouTubeUrl(course.video_url)
  );

  // Merge YouTube progress into state
  useEffect(() => {
    if (ytProgress > videoProgress) setVideoProgress(ytProgress);
  }, [ytProgress, videoProgress]);

  const handleBeginQuiz = () => setStep(2);

  const handleSubmitAnswers = async (answers: Record<string, string>) => {
    if (!courseId) return;
    setSubmitting(true);
    try {
      const res = await api.safety.submitTrainingAttempt({
        course_id: courseId,
        answers,
      });
      const result: AttemptResult = res.data?.data ?? res.data;
      setAttemptResult(result);
      setStep(3);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setLimitReached(true);
        setStep(3);
      } else {
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to submit answers"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setStep(1);
    setAttemptResult(null);
    setVideoProgress(0);
    setLimitReached(false);
  };

  const handleBack = () => navigate("/safety/my-training");

  if (isLoading) {
    return (
      <div className="min-h-screen pb-[var(--bottom-chrome)] bg-background">
        <div className="border-b border-border px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/safety/my-training")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              Loading...
            </h1>
          </div>
        </div>
        <div className="text-center py-12 text-muted-foreground text-sm">
          Loading course...
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen pb-[var(--bottom-chrome)] bg-background">
        <div className="border-b border-border px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/safety/my-training")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              Course Not Found
            </h1>
          </div>
        </div>
        <div className="text-center py-12 text-muted-foreground text-sm">
          This course could not be found.
        </div>
      </div>
    );
  }

  // Step labels for breadcrumb
  const stepLabels = ["Introduction", "Quiz", "Results"];

  return (
    <div className="min-h-screen pb-[var(--bottom-chrome)] bg-background">
      {/* Action bar */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/safety/my-training")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              {course.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center gap-6">
          {stepLabels.map((label, idx) => {
            const stepNum = (idx + 1) as Step;
            const isActive = step === stepNum;
            const isDone = step > stepNum;
            return (
              <div key={label} className="flex items-center gap-2">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isDone
                      ? "bg-green-500 text-white"
                      : isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {isDone ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    stepNum
                  )}
                </span>
                <span
                  className={`text-sm font-medium ${
                    isActive
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-3xl">
        {/* Attempt limit reached state */}
        {limitReached && step === 3 && (
          <div className="space-y-6 text-center max-w-lg mx-auto">
            <div className="w-20 h-20 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                Daily Limit Reached
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                You've reached the maximum attempts for today. You can try
                again tomorrow.
              </p>
            </div>
            <button
              onClick={handleBack}
              className="h-9 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Back to My Training
            </button>
          </div>
        )}

        {/* Step 1: Intro */}
        {step === 1 && (
          <IntroStep
            course={course}
            videoProgress={videoProgress}
            onBeginQuiz={handleBeginQuiz}
            iframeRef={iframeRef}
            onVideoProgress={setVideoProgress}
          />
        )}

        {/* Step 2: Quiz */}
        {step === 2 && (
          <>
            {submitting ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Submitting your answers...
              </div>
            ) : (
              <QuizStep
                questions={course.questions}
                onSubmit={handleSubmitAnswers}
              />
            )}
          </>
        )}

        {/* Step 3: Results (when not limit-reached) */}
        {step === 3 && !limitReached && attemptResult && (
          <ResultsStep
            result={attemptResult}
            course={course}
            onRetry={handleRetry}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
};

export default TakeTrainingScreen;
